import React, { useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'next-i18next';
import { Box } from '@mui/material';
import Head from 'next/head';
import generateMetaData from 'lib/utils/generateMetaData';
import { useSelector } from 'react-redux';
import useRenderAiBot from 'views/common/hooks/useRenderAiBot';
import StyledBox, {
  StyledStickNav,
} from 'views/rebranded-module/location-module/LocationViewV2/LocationViewV2.styles';
import { HeroSection } from 'views/rebranded-module/location-module/LocationViewV2/components/HeroSection';
import { useLocationCMSData } from 'views/rebranded-module/location-module/LocationViewV2/hooks/useLocationCMSData';
import { LazyLoad } from 'views/common/components';
import {
  ToursSection,
  ToursSectionSkeleton,
} from 'views/rebranded-module/location-module/LocationViewV2/components/ToursSection';
import {
  RiFootprintLine,
  RiMotorbikeLine,
  RiPriceTag3Line,
  RiCalendarTodoLine,
  RiInformationLine,
} from 'react-icons/ri';
import { interpolateString } from 'views/rebranded-module/common/utils/interpolateString';
import {
  AboutSection,
  DealsSection,
  RentalsSection,
  EventAndRidesSectionV2,
  MapSection,
  AboutSectionSkeleton,
} from 'views/rebranded-module/location-module/LocationViewV2/components';
import useAppNavigator from 'views/rebranded-module/common/hooks/useAppNavigator';
import { ContentContext } from 'views/rebranded-module/common/context/useContent';

const LocationViewV2 = (props) => {
  const { serverData, initWidgetData, cmsData: serverCmsData } = props;
  const { hreflangData } = useSelector((state) => state.globalData);
  const appNavigator = useAppNavigator();
  const stickyContainerRef = useRef(null);

  const { t } = useTranslation('locationV2_page');
  useRenderAiBot(true);
  const { data: cmsData } = useLocationCMSData();

  // mergedCmsData: serverCmsData takes precedence, fallback to cmsData
  const mergedCmsData = useMemo(
    () => (serverCmsData
      ? {
        ...cmsData,
        ...serverCmsData,
        LocationPage: {
          ...cmsData?.LocationPage,
          ...serverCmsData?.LocationPage,
        },
        RentalsCardComponent: {
          ...cmsData?.RentalsCardComponent,
          ...serverCmsData?.RentalsCardComponent,
        },
        DealCardComponent: {
          ...cmsData?.DealCardComponent,
          ...serverCmsData?.DealCardComponent,
        },
        ExpandableSectionComponent: {
          ...cmsData?.ExpandableSectionComponent,
          ...serverCmsData?.ExpandableSectionComponent,
        },
        TourCardComponent: {
          ...cmsData?.TourCardComponent,
          ...serverCmsData?.TourCardComponent,
        },
        MainWidgetComponent: {
          ...cmsData?.MainWidgetComponent,
          ...serverCmsData?.MainWidgetComponent,
        },
        RentalWidgetComponent: {
          ...cmsData?.RentalWidgetComponent,
          ...serverCmsData?.RentalWidgetComponent,
        },
        TourWidgetComponent: {
          ...cmsData?.TourWidgetComponent,
          ...serverCmsData?.TourWidgetComponent,
        },
        EventRideCardComponent: {
          ...cmsData?.EventRideCardComponent,
          ...serverCmsData?.EventRideCardComponent,
        },
      }
      : cmsData),
    [cmsData, serverCmsData],
  );

  const stickNavTabs = useMemo(() => {
    if (!serverData || !mergedCmsData?.LocationPage) return [];
    return [
      serverData.current_country_tours?.location_tours?.length > 0 && {
        id: 'tours',
        label: mergedCmsData?.LocationPage?.tab1,
        icon: <RiFootprintLine />,
      },
      serverData.vehicle_models_component?.motorcycles.length > 0 && {
        id: 'motorcycle-rentals',
        label: mergedCmsData?.LocationPage?.tab2,
        icon: <RiMotorbikeLine />,
      },
      serverData.hot_deals.length > 0 && {
        id: 'deals',
        label: mergedCmsData?.LocationPage?.tab3,
        icon: <RiPriceTag3Line />,
      },
      (serverData.events.length > 0 || serverData.attractions.length > 0) && {
        id: 'event',
        label: mergedCmsData?.LocationPage?.tab4,
        icon: <RiCalendarTodoLine />,
      },
      {
        id: 'about',
        label: mergedCmsData?.LocationPage?.tab5,
        icon: <RiInformationLine />,
      },
    ].filter(Boolean);
  }, [serverData, mergedCmsData]);

  useEffect(() => {
    const globalNav = document.querySelector('.NBAppBar');
    const stickyNav = stickyContainerRef.current;

    const handleScroll = () => {
      if (!globalNav || !stickyNav) return;

      const stickyNavRect = stickyNav.getBoundingClientRect();
      const globalNavHeight = globalNav.offsetHeight;
      const shouldBeStatic = stickyNavRect.top <= globalNavHeight && stickyNavRect.bottom > 0;
      globalNav.classList.toggle('NBStaticPosition', shouldBeStatic);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <StyledBox className="LVContiner">
      <ContentContext.Provider value={mergedCmsData}>
        <Head>
          {generateMetaData(serverData.seo_values)}
          {hreflangData && (
            <>
              <link rel="canonical" href={hreflangData?.canonical} />
              {hreflangData.urls?.map((local) => (
                <link
                  key={local.hreflang}
                  rel="alternate"
                  href={local.url}
                  hrefLang={local.hreflang}
                />
              ))}
            </>
          )}
        </Head>
        <Box>
          <HeroSection
            content={mergedCmsData?.LocationPage}
            coverImage={serverData.cover_image_url}
            title={interpolateString(
              mergedCmsData?.LocationPage?.heroTitle || 'Explore %{location}',
              {
                location: serverData.location.display_name,
              },
            )}
            // subTitle={serverData.location.subtitle}
            initWidgetData={initWidgetData}
            reservationData={{
              pickup_location_id: serverData.location.id,
              dropoff_location_id: serverData.location.id,
            }}
            reInitWidget={appNavigator.query.slug_1}
          />
          <Box ref={stickyContainerRef}>
            <StyledStickNav
              stickNavTabs={stickNavTabs}
              lazyLoading
              lazyLoadingSleepTime={400}
            />
            {serverData.current_country_tours?.location_tours?.length > 0 && (
              <Box id="tours" pt={8} pb={16}>
                <ToursSection content={mergedCmsData} data={serverData} />
              </Box>
            )}
            {serverData.vehicle_models_component?.motorcycles.length > 0 && (
              <Box id="motorcycle-rentals" className="LVRentalsSection">
                <LazyLoad
                  skeleton={<ToursSectionSkeleton />}
                  dataLoaded={!!serverData}>
                  <RentalsSection content={mergedCmsData} data={serverData} />
                </LazyLoad>
              </Box>
            )}
            {serverData.hot_deals.length > 0 && (
              <Box id="deals" className="LVSectionSpacing">
                <LazyLoad
                  skeleton={<ToursSectionSkeleton />}
                  dataLoaded={!!serverData}>
                  <DealsSection data={serverData} content={mergedCmsData} />
                </LazyLoad>
              </Box>
            )}
            {(serverData.events.length > 0
              || serverData.attractions.length > 0) && (
              <Box id="event" className="LVSectionSpacing LVEventRideSection">
                <LazyLoad
                  skeleton={<ToursSectionSkeleton />}
                  dataLoaded={!!serverData}>
                  <EventAndRidesSectionV2
                    content={mergedCmsData?.LocationPage}
                    rides={serverData.attractions}
                    events={serverData.events}
                    location={serverData.location}
                  />
                </LazyLoad>
              </Box>
            )}
            <Box id="about">
              <LazyLoad
                skeleton={<AboutSectionSkeleton />}
                dataLoaded={!!serverData}>
                <AboutSection
                  desc={serverData.location.description}
                  image={
                    serverData.location.location_image?.medium_url
                    ?? serverData.location.image
                  }
                  title={
                    interpolateString(mergedCmsData?.LocationPage?.aboutTitle, {
                      location: serverData.location.display_name,
                    })
                    || t('about_location', {
                      location: serverData.location.display_name,
                    })
                  }
                  content={mergedCmsData?.ExpandableSectionComponent}
                />
              </LazyLoad>
            </Box>
          </Box>
          <LazyLoad
            skeleton={<AboutSectionSkeleton />}
            dataLoaded={!!serverData}>
            <Box className="LVSectionSpacing">
              <MapSection
                content={mergedCmsData?.LocationPage}
                phones={serverData.phones}
                dealer={serverData.dealer}
                location={serverData.location}
              />
            </Box>
          </LazyLoad>
        </Box>
      </ContentContext.Provider>
    </StyledBox>
  );
};
export { LocationViewV2 };
